import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { LanguageGate } from './components/LanguageGate';
import { GA4Tracker } from './components/GA4Tracker';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { ChatWidget } from './components/sections/ChatWidget';
import HomePage from './pages/HomePage';
import SolutionsPage from './pages/SolutionsPage';
import PortfolioPage from './pages/PortfolioPage';
import PartnerPage from './pages/PartnerPage';
import PartnerDetailPage from './pages/PartnerDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ProduktKatalogPage from './pages/ProduktKatalogPage';
import CourseDetailPage from './pages/CourseDetailPage';
import DownloadsPage from './pages/DownloadsPage';
import DatenschutzPage from './pages/DatenschutzPage';
import ImpressumPage from './pages/ImpressumPage';
import BookingPage from './pages/BookingPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import VisitorSystemPage from './pages/VisitorSystemPage';
import InstructionSystemPage from './pages/InstructionSystemPage';
import CaseStudyInstructionSystem from './pages/CaseStudyInstructionSystem';
import CaseStudyVisitorSystem from './pages/CaseStudyVisitorSystem';
import CaseStudyCyberSecurity from './pages/CaseStudyCyberSecurity';
import { FAQOverviewPage, FAQDetailPage } from './pages/FAQPage';
import NotFoundPage from './pages/NotFoundPage';
import { RouteNormalizer } from './components/RouteNormalizer';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-36 right-5 z-50 w-11 h-11 rounded-full bg-[#0D2440] text-white shadow-lg flex items-center justify-center hover:bg-[#162D4A] transition-colors"
      data-testid="scroll-to-top-btn"
      aria-label="Scroll to top"
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
}

/**
 * Wraps the whole app tree under a language-prefixed BrowserRouter.
 * All internal <Link to="/about"> automatically resolve to /de/about or /en/about.
 */
function LocalizedApp() {
  const { language } = useLanguage();
  return (
    <BrowserRouter basename={`/${language}`}>
      <ScrollToTop />
      <RouteNormalizer />
      <GA4Tracker />
      <div className="App font-sans">
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/produktkatalog" element={<ProduktKatalogPage />} />
            <Route path="/course/:id" element={<CourseDetailPage />} />
            <Route path="/visitor-system" element={<VisitorSystemPage />} />
            <Route path="/instruction-system" element={<InstructionSystemPage />} />
            <Route path="/case-study/instruction-system" element={<CaseStudyInstructionSystem />} />
            <Route path="/case-study/visitor-system" element={<CaseStudyVisitorSystem />} />
            <Route path="/case-study/cyber-security" element={<CaseStudyCyberSecurity />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/partner/:partnerId" element={<PartnerDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/privacy-policy" element={<DatenschutzPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/imprint" element={<ImpressumPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/booking/confirmation" element={<BookingConfirmationPage />} />
            <Route path="/faq" element={<FAQOverviewPage />} />
            <Route path="/faq/:faqId" element={<FAQDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
        <ScrollToTopButton />
        <ChatWidget />
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageGate>
        <LanguageProvider>
          <LocalizedApp />
        </LanguageProvider>
      </LanguageGate>
    </HelmetProvider>
  );
}

export default App;
